/**
 * AggregationPicker — Dropdown for selecting an aggregation function for a column.
 *
 * Shows "+ Calculate" when no aggregation is selected. When selected, shows the
 * computed value (or a loading/error state) with an option to change or clear.
 *
 * Uses react-aria-components Menu/MenuTrigger inside the base Dropdown.Popover
 * (non-modal, closes on outside click inside stacked surfaces).
 */

import { AlertCircle, Check, Plus, XClose } from '@untitledui/icons';
import { useCallback } from 'react';
import type { Key } from 'react-aria-components';
import {
  Button as AriaButton,
  Menu as AriaMenu,
  MenuItem as AriaMenuItem,
  MenuTrigger as AriaMenuTrigger,
  Separator as AriaSeparator,
  Tooltip as AriaTooltip,
  TooltipTrigger as AriaTooltipTrigger,
} from 'react-aria-components';

import { Dropdown } from '@/lib/base/overlays/dropdown';
import { isTransientError } from '@/lib/errors';
import { cx } from '@/utils/cx';

/**
 * Overrides Dropdown.Popover's default w-62 / rounded-lg / overflow-auto panel.
 * Surface, ring, shadow, animation and z-index come from the wrapper.
 */
const AGGREGATION_POPOVER_CLASS = 'w-40 overflow-hidden rounded-md';

/**
 * react-aria's default containerPadding — Dropdown.Popover defaults to 16 for
 * kebab menus; keep this picker's original viewport-edge behaviour.
 */
const AGGREGATION_POPOVER_CONTAINER_PADDING = 12;

/** Maps aggregation function keys to human-readable labels */
const AGGREGATION_LABELS: Record<string, string> = {
  sum: 'Sum',
  avg: 'Average',
  min: 'Minimum',
  max: 'Maximum',
  count: 'Count',
  count_empty: 'Count empty',
  count_filled: 'Count filled',
  percent_empty: '% empty',
  percent_filled: '% filled',
};

export interface AggregationPickerProps {
  /** Aggregation keys allowed for this column's field type */
  allowedAggregations: string[];
  /** Currently selected aggregation key, or null if none */
  selectedAggregation: string | null;
  /** Callback when user picks an aggregation (null = clear) */
  onSelect: (aggregation: string | null) => void;
  /** Whether the aggregation value is currently loading */
  isLoading?: boolean;
  /** Whether there was an error fetching the aggregation value */
  hasError?: boolean;
  /** The raw error from the aggregations query, used to classify transient vs hard failures */
  error?: unknown;
  /** The computed aggregation value to display (string for display) */
  value?: string | null;
}

/**
 * Skeleton shimmer for loading state.
 */
function AggregationSkeleton() {
  return (
    <div
      className='h-3.5 w-12 rounded animate-pulse bg-[var(--color-bg-tertiary)]'
      aria-hidden='true'
    />
  );
}

/**
 * AggregationPicker lets users select an aggregation function for a column's totals row.
 *
 * State machine:
 *   no selection → "+ Calculate" button
 *   loading      → skeleton shimmer
 *   success      → value with change/clear options
 *   error        → "—" with subtle error icon and tooltip
 */
export function AggregationPicker({
  allowedAggregations,
  selectedAggregation,
  onSelect,
  isLoading = false,
  hasError = false,
  error,
  value = null,
}: AggregationPickerProps) {
  const selectedLabel = selectedAggregation
    ? (AGGREGATION_LABELS[selectedAggregation] ?? selectedAggregation)
    : null;

  // No selection: show "+ Calculate" trigger
  if (!selectedAggregation) {
    return (
      <AriaMenuTrigger>
        <AriaButton
          aria-label='Summarize'
          className={cx(
            'flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium',
            'text-[var(--color-text-tertiary)] hover:text-[var(--color-text-secondary)]',
            'hover:bg-[var(--color-bg-tertiary)]',
            'transition-colors duration-100 cursor-pointer outline-none',
            'focus-visible:ring-2 focus-visible:ring-[var(--color-brand-600)] focus-visible:ring-offset-1'
          )}
        >
          <Plus className='size-3 shrink-0' />
          <span>Summarize</span>
        </AriaButton>

        <Dropdown.Popover
          placement='bottom start'
          containerPadding={AGGREGATION_POPOVER_CONTAINER_PADDING}
          className={AGGREGATION_POPOVER_CLASS}
        >
          <AggregationMenu
            allowedAggregations={allowedAggregations}
            selectedAggregation={null}
            onSelect={onSelect}
          />
        </Dropdown.Popover>
      </AriaMenuTrigger>
    );
  }

  // Selected + loading: show skeleton only (no label to avoid janky layout)
  if (isLoading) {
    return (
      <div className='flex items-center px-1.5 py-0.5'>
        <AggregationSkeleton />
      </div>
    );
  }

  // Selected + error: show label and "—" with error icon
  if (hasError) {
    return (
      <div className='flex items-center gap-1 group/agg'>
        <AriaMenuTrigger>
          <AriaButton
            aria-label={`Change aggregation (${selectedLabel})`}
            className={cx(
              'flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium',
              'text-[var(--color-text-tertiary)] hover:text-[var(--color-text-secondary)]',
              'hover:bg-[var(--color-bg-tertiary)]',
              'transition-colors duration-100 cursor-pointer outline-none',
              'focus-visible:ring-2 focus-visible:ring-[var(--color-brand-600)] focus-visible:ring-offset-1'
            )}
          >
            <span>{selectedLabel}</span>
            <span className='text-[var(--color-text-tertiary)]'>—</span>
            <AriaTooltipTrigger delay={400}>
              <AriaButton
                aria-label='Aggregation error'
                className='outline-none'
              >
                <AlertCircle className='size-3 text-[var(--color-text-error-primary)] shrink-0' />
              </AriaButton>
              <AriaTooltip className='z-50 px-2 py-1 text-xs rounded bg-[var(--color-bg-secondary)] text-[var(--color-text-secondary)] shadow ring-1 ring-secondary_alt'>
                {isTransientError(error)
                  ? 'Trouble connecting…'
                  : "We couldn't load aggregation"}
              </AriaTooltip>
            </AriaTooltipTrigger>
          </AriaButton>

          <Dropdown.Popover
            placement='bottom start'
            containerPadding={AGGREGATION_POPOVER_CONTAINER_PADDING}
            className={AGGREGATION_POPOVER_CLASS}
          >
            <AggregationMenu
              allowedAggregations={allowedAggregations}
              selectedAggregation={selectedAggregation}
              onSelect={onSelect}
            />
          </Dropdown.Popover>
        </AriaMenuTrigger>
      </div>
    );
  }

  // Selected + success: show label + value with change/clear via dropdown
  return (
    <AriaMenuTrigger>
      <AriaButton
        aria-label={`Change aggregation (${selectedLabel})`}
        className={cx(
          'flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium max-w-full',
          'text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]',
          'hover:bg-[var(--color-bg-tertiary)]',
          'transition-colors duration-100 cursor-pointer outline-none',
          'focus-visible:ring-2 focus-visible:ring-[var(--color-brand-600)] focus-visible:ring-offset-1'
        )}
      >
        <span className='text-[var(--color-text-tertiary)] shrink-0'>
          {selectedLabel}
        </span>
        <span
          className='font-semibold overflow-hidden text-ellipsis whitespace-nowrap'
          title={value ?? undefined}
        >
          {value ?? '—'}
        </span>
      </AriaButton>

      <Dropdown.Popover
        placement='bottom start'
        containerPadding={AGGREGATION_POPOVER_CONTAINER_PADDING}
        className={AGGREGATION_POPOVER_CLASS}
      >
        <AggregationMenu
          allowedAggregations={allowedAggregations}
          selectedAggregation={selectedAggregation}
          onSelect={onSelect}
        />
      </Dropdown.Popover>
    </AriaMenuTrigger>
  );
}

/** Internal menu shared across all trigger states */
interface AggregationMenuProps {
  allowedAggregations: string[];
  selectedAggregation: string | null;
  onSelect: (aggregation: string | null) => void;
}

function AggregationMenu({
  allowedAggregations,
  selectedAggregation,
  onSelect,
}: AggregationMenuProps) {
  const handleAction = useCallback(
    (key: Key) => {
      if (key === 'clear') {
        onSelect(null);
      } else {
        onSelect(key as string);
      }
    },
    [onSelect]
  );

  return (
    <AriaMenu
      aria-label='Aggregation options'
      onAction={handleAction}
      className='py-0.5 outline-none'
    >
      {allowedAggregations.map(agg => (
        <AriaMenuItem
          key={agg}
          id={agg}
          textValue={AGGREGATION_LABELS[agg] ?? agg}
          className={cx(
            'px-1 outline-none cursor-pointer',
            'focus:bg-primary_hover'
          )}
        >
          <div className='flex items-center gap-1.5 px-2 py-1.5 rounded'>
            <span className='flex-1 text-xs font-medium text-[var(--color-text-secondary)]'>
              {AGGREGATION_LABELS[agg] ?? agg}
            </span>
            {selectedAggregation === agg && (
              <Check className='size-3 text-[var(--color-fg-brand-primary)] shrink-0' />
            )}
          </div>
        </AriaMenuItem>
      ))}

      {selectedAggregation && (
        <>
          <AriaSeparator className='my-0.5 h-px bg-border-secondary' />
          <AriaMenuItem
            id='clear'
            textValue='Clear'
            className={cx(
              'px-1 outline-none cursor-pointer',
              'focus:bg-primary_hover'
            )}
          >
            <div className='flex items-center gap-1.5 px-2 py-1.5 rounded'>
              <XClose className='size-3.5 text-fg-quaternary shrink-0' />
              <span className='flex-1 text-xs font-medium text-[var(--color-text-secondary)]'>
                Clear
              </span>
            </div>
          </AriaMenuItem>
        </>
      )}
    </AriaMenu>
  );
}
